import type { ColumnDef, EnemyDef, LevelDef, PickupDef, SegmentDef } from '../../types';

// OP-202 Cali Nocturna (GDD §10.4, §10.2, issue #108): Cali by night, written by hand with the format of §15.6. 600 u at
// 5.75 u/s (5.5 of world 2 plus 0.25 of level 2): takeoff, warm-up (20 %), tension (30 %) up to CP-202-A at x 300,
// respite (10 %), peak (40 %) and the clandestine airstrip. Gap columns every 8.5 u with the 3.7 u gap of world 2 and
// the neon skin, the gap centre never moving more than 3.0 u. Hazards: the surveillance antennas of the KGB of Cali
// (EN-22), standing on the ground (y 1.0) so that the zone (radius 3 u) reaches y 4.0 and the route through the gaps
// stays free; the first one comes alone after the warm-up with its two neighbouring gaps high (6 u or more from the
// antenna). Gallinazos, a paraglider and the search bloc appear only at the peak. The scapular of world 2 lies inside
// the zone of an antenna (a risk route). The Orejon cameo is data only for now (events `gilberto-gap` and
// `miguel-audit`); the runner, the models and the radio line come later (@gdd-pending). All x are local.

const column = (x: number, gapY: number): ColumnDef => ({ x, gapY, gapH: 3.7, skin: 'w2-neon' });
const dollar = (x: number, y: number): PickupDef => ({ x, y, type: 'dollar' });
const armor = (x: number, y: number): PickupDef => ({ x, y, type: 'armor' });
const bribe = (x: number, y: number): PickupDef => ({ x, y, type: 'bribe' });
const scapular = (x: number, y: number): PickupDef => ({ x, y, type: 'scapular' });
/** A surveillance antenna of Cali on the ground: its zone reaches 3 u above y 1.0. */
const antenna = (x: number): EnemyDef => ({ x, y: 1.0, type: 'EN-22' });
const gallinazo = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-01' });
const paraglider = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-04' });
/** A helicopter of the search bloc, hovering high. */
const searchBloc = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-02' });

const takeoff: SegmentDef = { id: 'w2-neon-takeoff-01', kind: 'takeoff', length: 40, columns: [], enemies: [], pickups: [] };

const warmup01: SegmentDef = {
  id: 'w2-neon-warmup-01',
  kind: 'pattern',
  phase: 'warmup',
  length: 50,
  columns: [
    column(20.0, 6.0),
    column(28.5, 6.5),
    column(37.0, 6.0),
    column(45.5, 5.5),
  ],
  enemies: [],
  pickups: [
    dollar(23.25, 6.0),
    dollar(24.25, 6.3),
    dollar(25.25, 6.0),
    dollar(40.25, 5.5),
    dollar(41.25, 5.8),
    dollar(42.25, 5.5),
  ],
};

const warmup02: SegmentDef = {
  id: 'w2-neon-warmup-02',
  kind: 'pattern',
  phase: 'warmup',
  length: 50,
  columns: [
    column(4.0, 6.0),
    column(12.5, 6.5),
    column(21.0, 6.0),
    column(29.5, 6.5),
    column(38.0, 6.0),
    column(46.5, 5.5),
  ],
  enemies: [],
  pickups: [
    dollar(15.75, 6.0),
    dollar(16.75, 6.3),
    dollar(17.75, 6.0),
    dollar(32.75, 6.0),
    dollar(33.75, 6.3),
    dollar(34.75, 6.0),
  ],
};

const tension01: SegmentDef = {
  id: 'w2-neon-tension-01',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [
    column(5.0, 7.0),
    column(13.5, 6.5),
    column(22.0, 7.5),
    column(30.5, 6.0),
    column(39.0, 6.5),
    column(47.5, 6.0),
  ],
  enemies: [antenna(17.75)],
  pickups: [
    dollar(8.25, 6.0),
    dollar(9.25, 6.3),
    dollar(10.25, 6.0),
    dollar(25.25, 6.5),
    dollar(26.25, 6.8),
    dollar(27.25, 6.5),
  ],
};

const tension02: SegmentDef = {
  id: 'w2-neon-tension-02',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [
    column(6.0, 7.0),
    column(14.5, 7.0),
    column(23.0, 5.5),
    column(31.5, 7.5),
    column(40.0, 6.0),
    column(48.5, 8.0),
  ],
  enemies: [antenna(27.25)],
  pickups: [
    dollar(17.75, 6.0),
    dollar(18.75, 6.3),
    dollar(19.75, 6.0),
    dollar(34.75, 6.5),
    dollar(35.75, 6.8),
    dollar(36.75, 6.5),
    armor(10.25, 6.5),
  ],
};

const tension03: SegmentDef = {
  id: 'w2-neon-tension-03',
  kind: 'pattern',
  phase: 'tension',
  length: 60,
  columns: [
    column(7.0, 5.5),
    column(15.5, 7.0),
    column(24.0, 7.0),
    column(32.5, 6.0),
    column(41.0, 6.5),
    column(49.5, 6.0),
    column(58.0, 8.0),
  ],
  enemies: [antenna(28.25), antenna(45.25)],
  pickups: [
    dollar(18.75, 6.75),
    dollar(19.75, 7.05),
    dollar(20.75, 6.75),
    dollar(52.75, 6.75),
    dollar(53.75, 7.05),
    dollar(54.75, 6.75),
    scapular(29.5, 2.4),
  ],
};

const respite01: SegmentDef = {
  id: 'w2-neon-respite-01',
  kind: 'pattern',
  phase: 'respite',
  length: 50,
  columns: [
    column(6.5, 6.0),
    column(15.0, 6.5),
    column(23.5, 6.0),
    column(32.0, 6.5),
    column(40.5, 6.0),
    column(49.0, 6.5),
  ],
  enemies: [],
  pickups: [
    dollar(18.25, 6.0),
    dollar(19.25, 6.3),
    dollar(20.25, 6.0),
    dollar(35.25, 6.0),
    dollar(36.25, 6.3),
    dollar(37.25, 6.0),
    bribe(10.75, 6.25),
  ],
};

const peak01: SegmentDef = {
  id: 'w2-neon-peak-01',
  kind: 'pattern',
  phase: 'peak',
  length: 65,
  columns: [
    column(7.5, 6.0),
    column(16.0, 6.5),
    column(24.5, 6.0),
    column(33.0, 7.0),
    column(41.5, 6.0),
    column(50.0, 7.5),
    column(58.5, 6.0),
  ],
  enemies: [antenna(12.25), gallinazo(36.0, 10.5), antenna(45.75)],
  pickups: [
    dollar(27.75, 5.75),
    dollar(28.75, 6.05),
    dollar(29.75, 5.75),
    dollar(53.25, 5.75),
    dollar(54.25, 6.05),
    dollar(55.25, 5.75),
  ],
};

const peak02: SegmentDef = {
  id: 'w2-neon-peak-02',
  kind: 'pattern',
  phase: 'peak',
  length: 65,
  columns: [
    column(2.0, 6.5),
    column(10.5, 6.0),
    column(19.0, 7.0),
    column(27.5, 6.0),
    column(36.0, 7.5),
    column(44.5, 6.0),
    column(53.0, 7.0),
    column(61.5, 8.0),
  ],
  enemies: [paraglider(14.0, 11.5), antenna(31.75), searchBloc(50.0, 11.2)],
  pickups: [
    dollar(13.75, 5.75),
    dollar(14.75, 6.05),
    dollar(15.75, 5.75),
    dollar(39.25, 5.75),
    dollar(40.25, 6.05),
    dollar(41.25, 5.75),
  ],
};

const peak03: SegmentDef = {
  id: 'w2-neon-peak-03',
  kind: 'pattern',
  phase: 'peak',
  length: 60,
  columns: [
    column(5.0, 8.0),
    column(13.5, 8.5),
    column(22.0, 6.0),
    column(30.5, 9.0),
    column(39.0, 6.5),
    column(47.5, 4.5),
  ],
  enemies: [gallinazo(20.0, 2.5), antenna(43.25)],
  pickups: [
    dollar(25.75, 7.5),
    dollar(26.75, 7.8),
    dollar(27.75, 7.5),
    dollar(50.75, 6.0),
    dollar(51.75, 6.3),
    dollar(52.75, 6.0),
  ],
};

const airstrip: SegmentDef = { id: 'w2-neon-airstrip-01', kind: 'airstrip', length: 60, columns: [], enemies: [], pickups: [] };

export const OP_202_SEGMENTS: readonly SegmentDef[] = [takeoff, warmup01, warmup02, tension01, tension02, tension03, respite01, peak01, peak02, peak03, airstrip];

export const OP_202: LevelDef = {
  id: '2-2',
  op: 'OP-202',
  name: 'Cali Nocturna',
  world: 2,
  scrollSpeed: 5.75,
  timeOfDay: 'night',
  segments: OP_202_SEGMENTS.map((segment) => segment.id),
  checkpoints: [{ id: 'CP-202-A', x: 300 }],
  ending: 'airstrip',
  // The Orejon cameo (GDD §10.4, #108): Gilberto moves the gap of a column once, announced 1.0 s before; Miguel removes
  // the dollars not yet collected of his stretch. Data only; the runner that resolves them comes later (@gdd-pending).
  events: [
    { id: 'gilberto-gap', fromX: 370, toX: 420 },
    { id: 'miguel-audit', fromX: 430, toX: 500 },
  ],
};
